/**
 * The outbound webhook catalog (TO-381): the domain events a tenant may
 * subscribe a partner URL to, the `data` fields each may carry out of TODAY,
 * and one sample event per type for the payload-shape preview.
 *
 * The field lists are the payload-shaping allowlist's universe. Each sample is
 * typed as its producer's payload interface, so a producer adding or renaming a
 * field breaks this file at compile time and the catalog test at run time —
 * the catalog changes deliberately, never silently (CONTRACT-307).
 */
import { AssignmentReason, CustomerSource, CustomerStatus, CustomerType } from '../customer/customer.interface.js'
import type {
	CustomerConvertedData,
	CustomerCreatedData,
	CustomerReassignedData,
	CustomerStatusChangedData,
	CustomerUpdatedData,
} from '../events/customer-events.types.js'
import type { OfferCreatedData, OfferOpenedData, OfferSentData } from '../events/offer-events.types.js'

/**
 * The subscribable event types. Values are the canonical `EventType`
 * strings (literal, so the enum has no reverse mapping; the catalog test pins
 * them to `EventType`).
 */
export enum OutboundEventType {
	CUSTOMER_CREATED = 'customer.created',
	CUSTOMER_UPDATED = 'customer.updated',
	CUSTOMER_REASSIGNED = 'customer.reassigned',
	CUSTOMER_CONVERTED = 'customer.converted',
	CUSTOMER_STATUS_CHANGED = 'customer.status_changed',
	OFFER_CREATED = 'offer.created',
	OFFER_SENT = 'offer.sent',
	OFFER_OPENED = 'offer.opened',
}

/** A catalog sample: the envelope fields an outbound body is built from. */
export interface OutboundSampleEvent<D = Record<string, unknown>> {
	eventId: string
	timestamp: string
	data: D
}

/** One catalog entry: the shapeable `data` fields, in outbound order, and a sample. */
export interface OutboundCatalogEntry {
	availableFields: readonly string[]
	sample: OutboundSampleEvent
}

/**
 * A customer payload as offered to partners: `origin` (TO-403) is internal
 * provenance and is deliberately not in the outbound allowlist.
 */
type Outbound<D> = Omit<D, 'origin'>

const entry = <D extends object>(
	availableFields: ReadonlyArray<keyof D & string>,
	sample: OutboundSampleEvent<D>,
): OutboundCatalogEntry => ({
	availableFields,
	// Copied into a plain record: the payload interfaces carry no index signature.
	sample: {
		eventId: sample.eventId,
		timestamp: sample.timestamp,
		data: Object.fromEntries(Object.entries(sample.data)),
	},
})

const SAMPLE_TIME = '2026-01-15T09:30:00.000Z'
const CUSTOMER_ID = '0190a1b2-c3d4-7e5f-8a9b-0c1d2e3f4a5b'
const OFFER_ID = '0190a1b2-c3d4-7e5f-8a9b-1c2d3e4f5a6b'
const USER_ID = '0190a1b2-c3d4-7e5f-8a9b-2c3d4e5f6a7b'
const OTHER_USER_ID = '0190a1b2-c3d4-7e5f-8a9b-3c4d5e6f7a8b'
const ORG_UNIT_ID = '0190a1b2-c3d4-7e5f-8a9b-4c5d6e7f8a9b'
const OTHER_ORG_UNIT_ID = '0190a1b2-c3d4-7e5f-8a9b-5c6d7e8f9a0b'

export const OUTBOUND_EVENT_CATALOG: Readonly<Record<OutboundEventType, OutboundCatalogEntry>> = {
	[OutboundEventType.CUSTOMER_CREATED]: entry<Outbound<CustomerCreatedData>>(
		['customerId', 'type', 'status', 'orgUnitId', 'assignedUserId', 'source'],
		{
			eventId: '00000000-0000-7000-8000-000000000001',
			timestamp: SAMPLE_TIME,
			data: {
				customerId: CUSTOMER_ID,
				type: CustomerType.LEAD,
				status: CustomerStatus.ACTIVE,
				orgUnitId: ORG_UNIT_ID,
				assignedUserId: USER_ID,
				source: CustomerSource.API,
			},
		},
	),
	[OutboundEventType.CUSTOMER_UPDATED]: entry<Outbound<CustomerUpdatedData>>(['customerId', 'changedFields'], {
		eventId: '00000000-0000-7000-8000-000000000002',
		timestamp: SAMPLE_TIME,
		data: { customerId: CUSTOMER_ID, changedFields: ['preferredChannel', 'status'] },
	}),
	[OutboundEventType.CUSTOMER_REASSIGNED]: entry<Outbound<CustomerReassignedData>>(
		['customerId', 'fromOrgUnitId', 'toOrgUnitId', 'fromUserId', 'toUserId', 'reason', 'assignmentId'],
		{
			eventId: '00000000-0000-7000-8000-000000000003',
			timestamp: SAMPLE_TIME,
			data: {
				customerId: CUSTOMER_ID,
				fromOrgUnitId: ORG_UNIT_ID,
				toOrgUnitId: OTHER_ORG_UNIT_ID,
				fromUserId: USER_ID,
				toUserId: OTHER_USER_ID,
				reason: AssignmentReason.MANUAL_REASSIGN,
				assignmentId: '0190a1b2-c3d4-7e5f-8a9b-6c7d8e9f0a1b',
			},
		},
	),
	[OutboundEventType.CUSTOMER_CONVERTED]: entry<Outbound<CustomerConvertedData>>(['customerId', 'convertedAt'], {
		eventId: '00000000-0000-7000-8000-000000000004',
		timestamp: SAMPLE_TIME,
		data: { customerId: CUSTOMER_ID, convertedAt: SAMPLE_TIME },
	}),
	[OutboundEventType.CUSTOMER_STATUS_CHANGED]: entry<Outbound<CustomerStatusChangedData>>(
		['customerId', 'previousStatus', 'status'],
		{
			eventId: '00000000-0000-7000-8000-000000000005',
			timestamp: SAMPLE_TIME,
			data: { customerId: CUSTOMER_ID, previousStatus: CustomerStatus.ACTIVE, status: CustomerStatus.INACTIVE },
		},
	),
	[OutboundEventType.OFFER_CREATED]: entry<OfferCreatedData>(['offerId', 'customerId', 'productId', 'createdBy'], {
		eventId: '00000000-0000-7000-8000-000000000006',
		timestamp: SAMPLE_TIME,
		data: {
			offerId: OFFER_ID,
			customerId: CUSTOMER_ID,
			productId: '0190a1b2-c3d4-7e5f-8a9b-7c8d9e0f1a2b',
			createdBy: USER_ID,
		},
	}),
	// The outbound shape stays the pre-TO-464 three fields; the snapshot id and
	// version added to the internal event (TO-464) and the `channels`, `customerId`
	// and `locale` routing keys (TO-465) are internal — not projected to tenant webhooks.
	[OutboundEventType.OFFER_SENT]: entry<Pick<OfferSentData, 'offerId' | 'landingUrl' | 'pdfCode'>>(
		['offerId', 'landingUrl', 'pdfCode'],
		{
			eventId: '00000000-0000-7000-8000-000000000007',
			timestamp: SAMPLE_TIME,
			data: { offerId: OFFER_ID, landingUrl: 'https://go.example.com/s/abc123', pdfCode: 'PDF-7Q2X9K' },
		},
	),
	[OutboundEventType.OFFER_OPENED]: entry<OfferOpenedData>(['offerId', 'renderId', 'openedAt'], {
		eventId: '00000000-0000-7000-8000-000000000008',
		timestamp: SAMPLE_TIME,
		data: { offerId: OFFER_ID, renderId: '0190a1b2-c3d4-7e5f-8a9b-8c9d0e1f2a3b', openedAt: SAMPLE_TIME },
	}),
}

/** The catalog types in catalog order (the order shape lists are returned in). */
export const OUTBOUND_EVENT_TYPES: readonly OutboundEventType[] = Object.values(OutboundEventType)

const OUTBOUND_TYPE_SET: ReadonlySet<string> = new Set(OUTBOUND_EVENT_TYPES)

/** Whether a string names a subscribable outbound event type. */
export const isOutboundEventType = (value: string): value is OutboundEventType => OUTBOUND_TYPE_SET.has(value)
